import type { BotKind, Resource, Terrain } from '../engine';

/**
 * Player colours, chosen so every pair is at least 16 apart in OKLab (×100):
 * the first six are the classic pieces, the rest picked by maximin search.
 * Twenty colours can't all be told apart with colour-blindness, so names and
 * board highlighting carry identity too.
 */
export const PLAYER_PALETTE: readonly { name: string; hex: string }[] = [
  { name: 'Red', hex: '#d62828' },
  { name: 'Blue', hex: '#1d4ed8' },
  { name: 'Orange', hex: '#f77f00' },
  { name: 'White', hex: '#f4f1de' },
  { name: 'Green', hex: '#2b9348' },
  { name: 'Brown', hex: '#7f4f24' },
  { name: 'Violet', hex: '#dd00ff' },
  { name: 'Lime', hex: '#00ff00' },
  { name: 'Navy', hex: '#002244' },
  { name: 'Sky', hex: '#77aaff' },
  { name: 'Plum', hex: '#880088' },
  { name: 'Pink', hex: '#ff88dd' },
  { name: 'Mint', hex: '#22ddbb' },
  { name: 'Yellow', hex: '#ddcc00' },
  { name: 'Mauve', hex: '#aa66aa' },
  { name: 'Maroon', hex: '#550000' },
  { name: 'Forest', hex: '#004400' },
  { name: 'Magenta', hex: '#ff0099' },
  { name: 'Purple', hex: '#8800ff' },
  { name: 'Teal', hex: '#006677' },
];

/** A palette entry's colour; past the palette (never chosen, but older games) hues step by the golden angle. */
export function paletteColor(index: number): string {
  const named = PLAYER_PALETTE[index];
  if (named) return named.hex;
  const hue = Math.round((index * 137.508 + 300) % 360);
  return `hsl(${hue}, 70%, ${index % 2 ? 38 : 55}%)`;
}

/** Colour for a seat when nobody chose one: the palette in order. */
export function playerColor(seat: number): string {
  return paletteColor(seat);
}

/** The first palette entries nobody has, for seats without a choice. */
export function defaultColors(count: number, taken: readonly number[] = []): number[] {
  const out: number[] = [];
  for (let i = 0; out.length < count; i++) if (!taken.includes(i) && !out.includes(i)) out.push(i);
  return out;
}

export const TERRAIN_COLORS: Record<Terrain, string> = {
  hills: '#c8553d',
  forest: '#2d6a4f',
  pasture: '#95d5b2',
  fields: '#e9c46a',
  mountains: '#8d99ae',
  desert: '#ddb892',
};

export const RESOURCE_COLORS: Record<Resource, string> = {
  brick: '#b94a32',
  lumber: '#2d6a4f',
  wool: '#95d5b2',
  grain: '#e9c46a',
  ore: '#8d99ae',
};

/** Text colour on each resource's background, picked for at least 4.5:1 contrast. */
export const RESOURCE_TEXT: Record<Resource, string> = {
  brick: '#ffffff',
  lumber: '#ffffff',
  wool: '#13241b',
  grain: '#1b1b1b',
  ore: '#1b1b1b',
};

export const RESOURCE_LABELS: Record<Resource, string> = {
  brick: 'Brick',
  lumber: 'Lumber',
  wool: 'Wool',
  grain: 'Grain',
  ore: 'Ore',
};

export const DEV_CARD_LABELS = {
  knight: 'Knight',
  victoryPoint: 'Victory Point',
  roadBuilding: 'Road Building',
  yearOfPlenty: 'Year of Plenty',
  monopoly: 'Monopoly',
} as const;

export const BOT_LABELS: Record<BotKind, { label: string; detail: string }> = {
  basic: { label: 'Basic', detail: 'Builds whatever it can afford. Never offers trades.' },
  smart: { label: 'Smart', detail: 'Plans its builds, trades with players and the bank, and robs the leader.' },
};
